<script lang="ts">
  import RangeSlider from "svelte-range-slider-pips";
  import {
    Accordion,
    AccordionItem,
    Button,
    ButtonGroup,
    Toggle,
    Card,
    Input,
  } from "flowbite-svelte";
  import ElasticsearchAPIConnector from "@elastic/search-ui-elasticsearch-connector";
  // Type assertion for connector constructor
  const ElasticsearchConnector = ElasticsearchAPIConnector as any;
  import { userStore } from "../../../services/stores/user-store.js";
  import type { Filter } from "@elastic/search-ui";
  import { onMount, afterUpdate } from "svelte";
  import { userType } from "../../../helpers/helpers.js";
  import { UserType } from "../../../services/api/types.js";
  import {
    faBackward,
    faCaretLeft,
    faCaretRight,
    faForward,
    faBackwardStep,
    faForwardStep,
    faCircleXmark,
    faArrowDownAZ,
    faArrowDown91,
    faChevronDown,
    faChevronUp,
    faChevronRight,
    faMinus,
    faEquals,
    faBars,
    faComment,
    faSpinner,
    faFilter } from "@fortawesome/free-solid-svg-icons";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { createEventDispatcher } from "svelte";
  import { debounce } from "lodash";
  import { drawerStore, closeDrawer } from "../../../services/stores/side-drawer-store.js";
  import { selectedObjects } from "../../../services/stores/selected-objects-store.js";
  import { newChatSignal } from "../../../services/stores/new-chat-signal-store.js";
  import { getEnvironment } from "../../../config/env.js";
  import { postProcessQuery } from "../../../services/search/query-processor.js";
  import { logError, logInfo, logWarning } from "../../../services/logging/log-handler.js";
  import { getElasticSearchKey } from "../../../services/api/data-handler.js";
  import { handleTokenExpiration } from "../../../services/security/auth-handler.js";
  import { get } from "svelte/store";
  import { getProjectsByCustomer } from "../../../services/api/data-handler.js";
  import type { ProjectsData } from "../../../services/api/types.js";

  /* Facets Documentation:
    https://www.elastic.co/guide/en/app-search/current/facets.html#facets-range-facet.
  */

  type FilterType = "all" | "any" | "none";
  type GlobalFilter = {
    field: string;
    type: FilterType;
    values: any;
  };

  // Initialize with default values to ensure search works immediately
  let lowerYear = 1980;
  let upperYear = new Date().getFullYear();
  let values = [lowerYear, upperYear];
  let fromDate: string = convertValueToFullyear(lowerYear);
  let toDate: string = convertValueToFullyear(upperYear);
  let allDocumentsChecked = false;
  let indeterminate = false;
  let toggleChecked = false;

  const facetSize = 100;

  // First define the search configuration since this drives what fields we get
  const searchConfig = {
    search_fields: {
      attachment_title: { weight: 3 },
      attachment_content_type: {},
      attachment_content: {},
      kind: {},
      study: {},
      project_name: {},
      file_name: {},
      project_title: {},
      indication: {},
      therapeutic_area: {},
      project_directors: {},
      project_status: {},
      review_topics: {},
      business_unit: {},
      review_type: {},
      document_type: {},
      customer_code: {},
      stakeholders: {},
      time_horizon: {},
    },
    result_fields: {
      attachment_title: {},
      attachment_content_type: {},
      attachment_keywords: {},
      attachment_content: {
        raw: {},
        snippet: {
          size: 100,
          fallback: true,
        },
      },
      attachment_date: {},
      study: {},
      project_name: {},
      kind: {},
      business_unit: {},
      review_type: {},
      indication: {},
      therapeutic_area: {},
      id: {},
      file_name: {},
      document_type: {},
      project_id: {},
      customer_code: {},
      stakeholders: {},
      time_horizon: {},
      title: {},
      finalized: {},
      project_directors: {},
      project_status: {},
      project_title: {},
      review_topics: {},
      CTAnalysisApproach: {},
      CTDesign: {},
      DisorderOrSyndrome: {},
      Drug: {},
      Disease: {},
      CNS_Tumor_Type: {},
      Carcinoma_Type: {},
      Leukemia_Type: {},
      Lymphoma_Type: {},
      Melanoma: {},
      Sarcoma_Type: {},
      Biomarker: {},
      Treatment: {},
      Drug_BrandName: {},
      Medical_Device: {},
    },
    disjunctiveFacets: [
      "study.keyword",
      "kind.keyword",
      "attachment_content_type.keyword",
      "business_unit.keyword",
      "review_type.keyword",
      "indication.keyword",
      "therapeutic_area.keyword",
      // "attachment_date.keyword",
      "Drug.keyword",
      "Disease.keyword",
      "Drug.keyword",
      "project_name.keyword",
      "CNS_Tumor_Type.keyword",
      "Carcinoma_Type.keyword",
      "Leukemia_Type.keyword",
      "Lymphoma_Type.keyword",
      "Melanoma.keyword",
      "Sarcoma_Type.keyword",
      "Biomarker.keyword",
      "Treatment.keyword",
      "Drug_BrandName.keyword",
      "Medical_Device.keyword",
    ],
    facets: {
      "project_name.keyword": { type: "value", size: facetSize },
      "kind.keyword": { type: "value" },
      "study.keyword": { type: "value" },
      "business_unit.keyword": { type: "value" },
      "review_type.keyword": { type: "value" },
      "indication.keyword": { type: "value" },
      "therapeutic_area.keyword": { type: "value" },
      "attachment_content_type.keyword": { type: "value", size: facetSize },
      "attachment_date.keyword": {
        // TODO: deterimine if attachment_date needed here since we've implemented it as a custom facet.
        type: "range",
        size: 100, //The big size is to reduce what's returned since the range isn't used.
        ranges: [
          {
            name: "default",
            from: fromDate,
            to: toDate,
          },
        ],
      },
      "Disease.keyword": { type: "value", size: facetSize },
      "Drug.keyword": { type: "value", size: facetSize },
      "CNS_Tumor_Type.keyword": { type: "value", size: facetSize },
      "Carcinoma_Type.keyword": { type: "value", size: facetSize },
      "Leukemia_Type.keyword": { type: "value", size: facetSize },
      "Lymphoma_Type.keyword": { type: "value", size: facetSize },
      "Melanoma.keyword": { type: "value", size: facetSize },
      "Sarcoma_Type.keyword": { type: "value", size: facetSize },
      "Biomarker.keyword": { type: "value", size: facetSize },
      "Treatment.keyword": { type: "value", size: facetSize },
      "Drug_BrandName.keyword": { type: "value", size: facetSize },
      "Medical_Device.keyword": { type: "value", size: facetSize },
    },
    highlight: {
      fields: {
        attachment_content: {
          fragment_size: 500,
          number_of_fragments: 3,
          pre_tags: ["<em>"],
        },
        attachment_title: {
          number_of_fragments: 1, // Don't fragment the title
          pre_tags: ["<em>"],
          post_tags: ["</em>"],
        },
      },
    },
    autoCompleteQuery: {
      resultsPerPage: 5,
      result_fields: {
        business_unit: { snippet: { size: 100, fallback: true } },
        url: { raw: {} },
      },
      search_fields: {
        therapeutic_area_autocomplete: {},
      },
    },
  } as const; // Make this const to help with type inference

  // Then derive the types from the configuration
  type RequestedFields = {
    [K in keyof typeof searchConfig.result_fields]: string;
  };

  interface SearchResult {
    id: string;
    _meta: {
      id: string;
      rawHit: {
        _source: RequestedFields;
        highlight?: {
          attachment_content?: string[];
          attachment_title?: string[];
        };
      };
    };
  }

  interface FacetOption {
    value: string;
    count: number;
  }

  interface Facet {
    field: string;
    label: string;
    options: FacetOption[];
  }

  interface FacetVisibility {
    [key: string]: number;
  }

  export let customer_id: string;
  export let customer_code: string;

  const dispatch = createEventDispatcher();

  // Restore search criteria when customer_code changes
  // Note: This only runs when customer_code prop changes, not on initial mount
  let previousCustomerCode = customer_code;
  $: if (customer_code && customer_code !== previousCustomerCode) {
    previousCustomerCode = customer_code;
    // Update connector with new customer_code (index changes with customer_code)
    const apiKey = sessionStorage.getItem("elasticSearchKey") || localStorage.getItem("elasticSearchKey");
    connector = createConnector(apiKey);
    
    // Refetch projects data for the new customer
    if (customer_id) {
      getProjectsByCustomer(customer_id)
        .then(data => { projectsData = data; })
        .catch(err => logError(err, "ResultsSearch", undefined, { message: "Error fetching projects data on customer change" }));
    }
    
    const restored = restoreSearchCriteria();
    if (!restored) {
      // If no saved state, use defaults
      setDocumentDateRange();
      hasFetchedAfterRestore = true; // Mark that we've fetched
      fetchResults();
    }
    // If restored, the restore function will handle the single fetch
    // Don't call fetchResults() here to avoid double fetching
  }

  function getElasticIndex(customer_code: string): string {
    return `slr-${customer_code.toLowerCase()}`;
  }

  // Create connector function to allow recreation with updated API key
  function createConnector(apiKey: string | null): any {
    return new ElasticsearchConnector(
      {
        host: getEnvironment().elasticSearchHost,
        index: getElasticIndex(customer_code),
        apiKey: apiKey,
      },
      // postProcess the requestBody generated by the connector to modify query behavior
      (requestBody: any, requestState: any, queryConfig: any) =>
        postProcessQuery(requestBody, requestState, queryConfig, toggleChecked)
    );
  }

  // Initialize connector with current key
  let connector = createConnector(sessionStorage.getItem("elasticSearchKey"));

  // Self-healing function to ensure we have a valid elasticSearchKey
  async function ensureElasticSearchKey(): Promise<string | null> {
    // First, try sessionStorage
    let key = sessionStorage.getItem("elasticSearchKey");
    
    if (key) {
      return key;
    }
    
    // Fallback to localStorage
    key = localStorage.getItem("elasticSearchKey");
    
    if (key) {
      // Restore to sessionStorage for future use
      sessionStorage.setItem("elasticSearchKey", key);
      // Recreate connector with the restored key
      connector = createConnector(key);
      return key;
    }
    
    // If both are empty, try to refresh from API
    try {
      const newKey = await getElasticSearchKey();
      if (newKey) {
        // Store in both storages
        localStorage.setItem("elasticSearchKey", newKey);
        sessionStorage.setItem("elasticSearchKey", newKey);
        // Recreate connector with the new key
        connector = createConnector(newKey);
        return newKey;
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Failed to refresh elasticSearchKey");
      logError(error, "ResultsSearch", undefined, { message: "Failed to refresh elasticSearchKey" });
      // If the token/session is expired, redirect to login to re-authenticate
      if (err.name === "TokenExpiredError" || err.message?.toLowerCase().includes("token has expired")) {
        await handleTokenExpiration();
        return null;
      }
    }
    
    return null;
  }

  let searchQuery = "";
  let currentPage = 1;
  let resultsPerPage = 25;
  let selectedFacets: Record<string, string[]> = {};
  let results: SearchResult[] = [];
  let totalResults = 0;
  let isLoading = false;
  let hasError = false;
  let errorMessage = "";
  let facetSortByCount = true;
  let loadingTimeout: number | undefined;
  let batchSize = 5;
  let currentBatch = 1;
  let visiblePages: number[] = [];
  //TODO: remove since the idea of using it to filter customers doesn't work. Kept in case we need a way to add to query this way
  let extraQueryString = "";
  let facetFilter = "";
  let facetOpenState: boolean[] = [];
  let isRestoring = false;
  let isInitialized = false;
  let hasFetchedAfterRestore = false;
  let isInitialLoad = true; // Track if this is the first load/restore
  let isBulkSelecting = false; // Track bulk selection operations
  let matchingDocumentKeys: Set<string> = new Set(); // Track document keys that match current search
  let projectsData: ProjectsData | null = null; // Store project data for document counts

  // Storage key helper function
  function getStorageKey(customer_code: string): string {
    return `results_explorer_search_${customer_code}`;
  }

  // Save search criteria to sessionStorage
  function saveSearchCriteria() {
    if (isRestoring || !isInitialized) {
      return; // Don't save during restoration or before initialization - acts as a guard
    }

    try {
      const searchState = {
        text: searchQuery || "",
        facets: {
          filters: facetFilter || "",
          order: facetSortByCount ? "count" : "alphabetical",
          sort_by: "", // Not used in current implementation, but preserve structure
          selected: JSON.stringify(selectedFacets),
        },
        results: {
          sort_by: selectedSort || "_score",
          view_by: viewMode || "compact",
          showing: resultsPerPage || 25,
          page: currentPage || 1,
        },
        matchAll: toggleChecked,
        dateRange: values || [lowerYear, upperYear],
        facetOpenState: facetOpenState
          .map((isOpen, index) => (isOpen ? index : null))
          .filter((index) => index !== null) as number[], // Save only expanded indices
      };

      const key = getStorageKey(customer_code);
      const jsonString = JSON.stringify(searchState);
      sessionStorage.setItem(key, jsonString);
    } catch (error) {
      logError(error, "ResultsSearch", undefined, { message: "Error saving search criteria" });
    }
  }

  // Restore search criteria from sessionStorage
  function restoreSearchCriteria(): boolean {
    try {
      const key = getStorageKey(customer_code);
      const stored = sessionStorage.getItem(key);
      if (!stored) {
        return false;
      }

      isRestoring = true;
      const searchState = JSON.parse(stored);

      // Restore basic search fields
      if (searchState.text !== undefined && searchState.text !== null) {
        searchQuery = String(searchState.text);
      }
      if (searchState.matchAll !== undefined) {
        toggleChecked = searchState.matchAll;
      }

      // Restore facet settings
      if (searchState.facets) {
        if (searchState.facets.filters !== undefined) {
          facetFilter = searchState.facets.filters;
        }
        if (searchState.facets.order !== undefined) {
          facetSortByCount = searchState.facets.order === "count";
        }
        if (searchState.facets.selected) {
          try {
            selectedFacets = JSON.parse(searchState.facets.selected);
          } catch (e) {
            logError(e, "ResultsSearch", undefined, { message: "Error parsing selected facets" });
            selectedFacets = {};
          }
        }
      }

      // Restore results settings FIRST (especially page number) before date range
      // This ensures the page is correct before any reactive statements fire
      if (searchState.results) {
        if (searchState.results.sort_by !== undefined) {
          selectedSort = searchState.results.sort_by;
        }
        if (searchState.results.view_by !== undefined) {
          viewMode = searchState.results.view_by;
        }
        if (searchState.results.showing !== undefined) {
          resultsPerPage = searchState.results.showing;
        }
        // Restore page number immediately - MUST be before date range to prevent wrong page fetch
        if (searchState.results.page !== undefined) {
          currentPage = searchState.results.page;
          lastRestoreTime = Date.now();
        }
      }

      // Restore date range AFTER page number - only restore the selected values (slider handles), not the bounds
      // The slider bounds (lowerYear/upperYear) should always be 1980 to current year
      // Only the values array (selected range) should be restored
      if (searchState.dateRange && Array.isArray(searchState.dateRange) && searchState.dateRange.length === 2) {
        const restoredStartYear = searchState.dateRange[0];
        const restoredEndYear = searchState.dateRange[1];
        // Only restore the selected values, not the bounds
        // Ensure values are within the slider bounds
        const minYear = Math.max(1980, Math.min(restoredStartYear, upperYear));
        const maxYear = Math.min(upperYear, Math.max(restoredEndYear, lowerYear));
        values = [minYear, maxYear];
        // Manually set the derived date values
        fromDate = convertValueToFullyear(minYear);
        toDate = convertValueToFullyear(maxYear);
      }

      // Store expanded facet indices to restore after facets are loaded
      // This will be handled in fetchResults when facets are available
      if (searchState.facetOpenState && Array.isArray(searchState.facetOpenState)) {
        // Store temporarily - will be restored in fetchResults
        (window as any).__pendingFacetOpenState = searchState.facetOpenState;
      }

      // Set isRestoring to false after a delay to prevent multiple fetches
      // This allows all reactive statements to see isRestoring=true and skip their fetches
      // Then we'll do a single fetch after everything is restored
      hasFetchedAfterRestore = false;
      setTimeout(() => {
        isRestoring = false;
        // Set the flag first, then fetch after a tiny delay to ensure reactive statements have processed
        hasFetchedAfterRestore = true;
        setTimeout(() => {
          fetchResults();
        }, 10);
      }, 100); // Delay to let all reactive statements see isRestoring=true
      return true;
    } catch (error) {
      logError(error, "ResultsSearch", undefined, { message: "Error restoring search criteria" });
      isRestoring = false;
      return false;
    }
  }

  let facets: Facet[] = [
    { field: "project_name.keyword", label: "Project", options: [] },
    { field: "kind.keyword", label: "SLR Stage", options: [] },
    { field: "business_unit.keyword", label: "Business Unit", options: [] },
    { field: "review_type.keyword", label: "Review Type", options: [] },
    {
      field: "therapeutic_area.keyword",
      label: "Therapeutic Area",
      options: [],
    },
    {
      field: "indication.keyword",
      label: "Indication",
      options: [],
    },
    {
      field: "attachment_content_type.keyword",
      label: "Content Type",
      options: [],
    },
    { field: "Disease.keyword", label: "Disease", options: [] },
    { field: "Drug.keyword", label: "Drug", options: [] },
    { field: "study.keyword", label: "Study", options: [] },
    { field: "CNS_Tumor_Type.keyword", label: "CNS Tumor Type", options: [] },
    { field: "Carcinoma_Type.keyword", label: "Carcinoma Type", options: [] },
    { field: "Leukemia_Type.keyword", label: "Leukemia Type", options: [] },
    { field: "Lymphoma_Type.keyword", label: "Lymphoma Type", options: [] },
    { field: "Melanoma.keyword", label: "Melanoma", options: [] },
    { field: "Sarcoma_Type.keyword", label: "Sarcoma Type", options: [] },
    { field: "Biomarker.keyword", label: "Biomarker", options: [] },
    { field: "Treatment.keyword", label: "Treatment", options: [] },
    { field: "Drug_BrandName.keyword", label: "Drug Brand Name", options: [] },
    { field: "Medical_Device.keyword", label: "Medical Device", options: [] },
    { field: "attachment_date", label: "Date", options: [] },
  ];

  $: {
    fromDate = convertValueToFullyear(values[0]);
    toDate = convertValueToFullyear(values[1]);
    if (fromDate && toDate) {
      // TODO: determine if this is needed. Making attachment_data a facet stops the connector from making it a post filter.
      selectedFacets["attachment_date"] = [fromDate, toDate];
      if (!isRestoring && isInitialized) {
        saveSearchCriteria();
      }
      // Only fetch results if not restoring and we've done the post-restore fetch (to avoid double fetch)
      if (!isRestoring && hasFetchedAfterRestore) {
        fetchResults();
      }
    }
  }

  // Save when facet open state changes
  $: if (facetOpenState && facetOpenState.length > 0 && !isRestoring && isInitialized) {
    saveSearchCriteria();
  }

  // Save when current page changes
  $: if (currentPage !== undefined && !isRestoring && isInitialized) {
    saveSearchCriteria();
  }
  const sortOptions = [
    {
      value: "_score",
      label: "Default",
      direction: "desc",
    },
    {
      value: "Disease.keyword",
      label: "Disease",
      direction: "asc",
    },
    {
      value: "study.keyword",
      label: "Study",
      direction: "asc",
    },
  ];
  let selectedSort = sortOptions[0].value;

  let initialVisibleItems = 20;
  let incrementAmount = 10;
  let visibleItems: FacetVisibility = {};
  let viewMode = "compact";

  function setDocumentDateRange() {
    // TODO: call ElasticSearch to get the min document date.
    const newLowerYear = 1980; // This could be from API
    const newUpperYear = new Date().getFullYear();

    // Always set the slider bounds to the full range (1980 to current year)
    // This is the min/max of the slider control itself
    if (newLowerYear !== lowerYear || newUpperYear !== upperYear) {
      lowerYear = newLowerYear;
      upperYear = newUpperYear;
      
      // Only set default selected values if we're not restoring and values are at defaults
      // The selected values (slider handles) are stored separately in the values array
      if (!isRestoring && (values[0] === 1980 && values[1] === new Date().getFullYear())) {
        values = [lowerYear, upperYear];
        fromDate = convertValueToFullyear(lowerYear);
        toDate = convertValueToFullyear(upperYear);
        selectedFacets["attachment_date"] = [fromDate, toDate];
        // Only fetch if initialized and we've done the post-restore fetch (to avoid double fetch)
        if (isInitialized && hasFetchedAfterRestore) {
          fetchResults();
        }
      }
    }
  }

  function initializeFacetVisibility(facetField: string) {
    if (!(facetField in visibleItems)) {
      visibleItems[facetField] = initialVisibleItems;
    }
  }

  function showMoreItems(facetField: string, e: Event) {
    e.preventDefault();
    visibleItems[facetField] += incrementAmount;
    visibleItems = { ...visibleItems };
  }

  function isRangeFacet(name: string): boolean {
    return (
      searchConfig.facets[
        (name + ".keyword") as keyof typeof searchConfig.facets
      ]?.type === "range"
    );
  }

  // Build Elasticsearch query from current search state
  function buildElasticsearchQuery(): any {
    // Build filter clauses
    const mustClauses: any[] = [];
    const filterClauses: any[] = [];

    // Handle search term with multi_match on search_fields
    if (searchQuery) {
      const searchFields = Object.keys(searchConfig.search_fields).map((field: string) => {
        const fieldConfig = searchConfig.search_fields[field as keyof typeof searchConfig.search_fields];
        const weight = (fieldConfig as any)?.weight;
        return weight && weight !== 1 ? `${field}^${weight}` : field;
      });

      const multiMatchQuery: any = {
        multi_match: {
          query: searchQuery,
          fields: searchFields,
          type: "best_fields",
          operator: toggleChecked ? "and" : "or",
        },
      };
      mustClauses.push(multiMatchQuery);
    }

    // Handle selected facets (filters)
    const selectedFilters = Object.entries(selectedFacets)
      .filter(([field, values]) => values.length > 0 && !field.includes("attachment_date"))
      .map(([field, values]) => {
        const keywordField = field.endsWith(".keyword") ? field : `${field}.keyword`;
        if (values.length === 1) {
          return { term: { [keywordField]: values[0] } };
        } else {
          return { terms: { [keywordField]: values } };
        }
      });
    filterClauses.push(...selectedFilters);

    // Handle global filters (attachment_date, document_type, project_status)
    filterClauses.push({
      range: {
        attachment_date: {
          gte: fromDate,
          lte: toDate,
        },
      },
    });

    // IMPORTANT: Results Explorer queries full_document type (created by Node.js savePipelineResults.ts)
    // which stores fields at root level. This differs from chunk documents (created by Python/LangChain)
    // which store fields under metadata.* prefix. Both schemas coexist in the same ES index.
    // See docs/design/elasticsearch-document-schema.md for details.
    filterClauses.push({
      term: {
        "document_type.keyword": "full_document",
      },
    });

    if (userType() === UserType.CUSTOMER) {
      filterClauses.push({
        term: {
          "project_status.keyword": "Active",
        },
      });
    }

    // Build query structure
    const query: any = {
      bool: {},
    };

    if (mustClauses.length > 0) {
      query.bool.must = mustClauses;
    }

    if (filterClauses.length > 0) {
      query.bool.filter = filterClauses;
    }

    return query;
  }

  // Fetch all matching document keys with minimal fields
  async function fetchAllMatchingDocumentKeys(): Promise<Array<{
    doc_key: string;
    file_name: string;
    doc_name: string;
    customer_code: string;
    project_id: string;
    project_name: string;
    stage: string;
  }>> {
    const apiKey = await ensureElasticSearchKey();
    if (!apiKey) {
      throw new Error("Unable to authenticate with search service");
    }

    const esUrl = getEnvironment().elasticSearchHost;
    const indexName = getElasticIndex(customer_code);
    const url = `${esUrl}/${indexName}/_search`;

    // Build Elasticsearch query
    const esQuery = buildElasticsearchQuery();

    // Build sort clause
    const sortClause: any[] = [];
    if (selectedSort && selectedSort !== "_score") {
      const sortOption = sortOptions.find((opt) => opt.value === selectedSort);
      if (sortOption) {
        sortClause.push({
          [selectedSort]: {
            order: sortOption.direction || "asc",
          },
        });
      }
    }
    // Add _score as secondary sort if not primary
    if (selectedSort !== "_score") {
      sortClause.push("_score");
    }

    const requestBody: any = {
      query: esQuery,
      _source: ["file_name", "attachment_title", "attachment_content_type", "project_id", "project_name", "customer_code", "kind"],
      size: 10000, // Elasticsearch default max for single request
      sort: sortClause.length > 0 ? sortClause : undefined,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `ApiKey ${apiKey}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Elasticsearch API error: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      const hits = data.hits?.hits || [];

      // Process results to extract minimal document data
      // Uses same logic as getDocumentTitle() for consistency
      const results = hits.map((hit: any) => {
        const source = hit._source;
        const doc_key = hit._id;
        const file_name_blob = `${doc_key}.${source.file_name?.split(".").pop() || "pdf"}`;

        // Derive doc_name using same logic as getDocumentTitle()
        let doc_name: string;
        const content_type = source.attachment_content_type || "";
        const containsOfficeApp =
          content_type.toLowerCase().includes("excel") ||
          content_type.toLowerCase().includes("word") ||
          content_type.toLowerCase().includes("powerpoint");

        if (containsOfficeApp) {
          doc_name = source.file_name || "Untitled";
        } else if (source.attachment_title && source.attachment_title.toLowerCase() !== "none") {
          // PDFs with title: append filename in brackets so extension can be extracted
          doc_name = source.attachment_title + " [" + source.file_name + "]";
        } else {
          doc_name = source.file_name || "Untitled";
        }

        return {
          doc_key: doc_key,
          file_name: file_name_blob,
          doc_name: doc_name,
          customer_code: source.customer_code || customer_code,
          project_id: source.project_id || "",
          project_name: source.project_name || "",
          stage: source.kind || "", // 'kind' field in ES maps to 'stage' in the app
        };
      });

      // Update matching document keys set
      matchingDocumentKeys = new Set(results.map((r: { doc_key: string }) => r.doc_key));

      return results;
    } catch (error) {
      logError(error, "ResultsSearch.fetchAllMatchingDocumentKeys", undefined, { customer_code, totalResults });
      throw errorObj;
    }
  }

  async function fetchResults() {
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
    }
    const fetchStartTime = Date.now();
    isLoading = true;
    hasError = false;
    errorMessage = "";

    // Self-heal: ensure we have a valid elasticSearchKey before searching
    const apiKey = await ensureElasticSearchKey();
    if (!apiKey) {
      hasError = true;
      errorMessage = "Unable to authenticate with search service. Please try logging out and back in.";
      isLoading = false;
      results = [];
      totalResults = 0;
      facetOpenState = [];
      logError(new Error("No elasticSearchKey available after self-healing attempt"), "ResultsSearch", undefined);
      return;
    }

    try {
      const selectedFilters = Object.entries(selectedFacets)
        .filter(
          ([field, values]) =>
            values.length > 0 && !field.includes("attachment_date")
        )
        .map(([field, values]) => {
          return {
            field: field,
            type: "any" as FilterType,
            values: values,
          };
        });
      let global_filters: GlobalFilter[] = [
        {
          field: "attachment_date",
          type: "all",
          values: [
            {
              from: fromDate,
              to: toDate,
            },
          ],
        },
        {
          field: "document_type.keyword",
          type: "all",
          values: ["full_document"],
        },
      ];
      if (userType() === UserType.CUSTOMER) {
        global_filters.push({
          field: "project_status.keyword",
          type: "all",
          values: ["Active"],
        });
      }
      extraQueryString = "";
      const requestState = {
        searchTerm: searchQuery,
        resultsPerPage: resultsPerPage,
        current: currentPage,
        filters: selectedFilters,
        sort: selectedSort
          ? [
              {
                field: selectedSort,
                direction:
                  sortOptions.find((opt) => opt.value === selectedSort)
                    ?.direction || "asc",
              },
            ]
          : [],
      };

      const queryConfig = {
        facets: searchConfig.facets,
        result_fields: searchConfig.result_fields,
        search_fields: searchConfig.search_fields,
        disjunctiveFacets: searchConfig.disjunctiveFacets,
        highlight: searchConfig.highlight,
        filters: global_filters,
      };

      const response = await connector.onSearch(requestState, queryConfig);
      
      // Check if response exists and has expected structure
      if (!response) {
        throw new Error("Search returned no response");
      }
      
      // Results
      if (response.results) {
        results = response.results.map((result: any) => ({
          id: result.id || result._meta?.id || Math.random().toString(),
          raw: result.raw || result,
          ...result,
        }));
      } else {
        results = [];
      }
      totalResults = response?.totalResults ?? 0;
      
      // Ensure spinner shows for minimum of 1 second
      const fetchDuration = Date.now() - fetchStartTime;
      const minDisplayTime = 1000; // 1 second
      const remainingTime = Math.max(0, minDisplayTime - fetchDuration);
      
      // Mark initial load as complete after first successful fetch
      if (isInitialLoad) {
        setTimeout(() => {
          isInitialLoad = false;
        }, remainingTime);
      }
      
      // For subsequent fetches, also ensure minimum display time
      if (remainingTime > 0) {
        setTimeout(() => {
          isLoading = false;
        }, remainingTime);
      } else {
        isLoading = false;
      }

      // Facets
      if (response.facets) {
        const facetCount = Object.keys(response.facets).length;
        if (facetOpenState.length === 0) {
          if (facetCount > 0) {
            facetOpenState = Array(facetCount).fill(false);
            // Restore expanded facets if we have pending state from restore
            const pendingState = (window as any).__pendingFacetOpenState;
            if (pendingState && Array.isArray(pendingState)) {
              pendingState.forEach((index: number) => {
                if (index >= 0 && index < facetOpenState.length) {
                  facetOpenState[index] = true;
                }
              });
              facetOpenState = [...facetOpenState]; // Trigger reactivity
              delete (window as any).__pendingFacetOpenState;
            }
          } else {
            facetOpenState = [];
          }
        }
        facets = facets.map((facet) => {
          const facetData = response.facets[facet.field];
          var options = [];
          options =
            facetData?.[0]?.data?.map(
              (item: { value: string; count: number }) => ({
                value: item.value,
                count: item.count,
              })
            ) || [];
          // Don't set isLoading = false here - it's handled after all processing
          return {
            ...facet,
            options: sortFacetOptions(options, facetSortByCount),
          };
        });
      } else {
        facetOpenState = [];
      }


    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      logError(error, "ResultsSearch.fetchResults", undefined);
      
      // Log error to Application Insights
      const filterCount = Object.entries(selectedFacets)
        .filter(([field, values]) => values.length > 0 && !field.includes("attachment_date"))
        .length;

      hasError = true;
      errorMessage = "Failed to fetch results";
      // Reset results and totalResults on error to prevent stale data
      results = [];
      totalResults = 0;
      facetOpenState = [];
    } finally {
      // Ensure spinner shows for minimum of 1 second even on error
      const fetchDuration = Date.now() - fetchStartTime;
      const minDisplayTime = 1000; // 1 second
      const remainingTime = Math.max(0, minDisplayTime - fetchDuration);
      
      if (remainingTime > 0) {
        setTimeout(() => {
          isLoading = false;
          if (isInitialLoad) {
            isInitialLoad = false;
          }
        }, remainingTime);
      } else {
        isLoading = false;
        if (isInitialLoad) {
          isInitialLoad = false;
        }
      }
    }
  }

  onMount(async () => {
    try {
      // Set isRestoring BEFORE any operations to prevent reactive statements from firing
      isRestoring = true;
      hasFetchedAfterRestore = false;
      
      // Fetch projects data to get document counts per project
      // This enables the store to detect when all documents for a project are selected
      if (customer_id) {
        try {
          projectsData = await getProjectsByCustomer(customer_id);
        } catch (projectsError) {
          logError(projectsError, "ResultsSearch", undefined, { message: "Error fetching projects data" });
          // Continue without projects data - selection will still work, just won't detect full project selection
        }
      }
      
      setDocumentDateRange();
    
      // Then restore saved criteria (this will set the selected values, not the bounds)
      const restored = restoreSearchCriteria();
      hasRestoredOnMount = restored;
      
      // Ensure bounds are still correct after restore (restore shouldn't change bounds)
      // The slider bounds should always be 1980 to current year
      const currentYear = new Date().getFullYear();
      if (lowerYear !== 1980 || upperYear !== currentYear) {
        lowerYear = 1980;
        upperYear = currentYear;
      }
      
      // Mark as initialized AFTER restore completes, so saves can happen
      isInitialized = true;
      
      // Fetch results - but only if we didn't restore
      // If we restored, the restore function will handle the single fetch
      if (!restored) {
        // Not restoring, so we can fetch immediately
        isRestoring = false;
        hasFetchedAfterRestore = true; // Mark that we've fetched (not restoring)
        fetchResults();
      }
      // If restored, isRestoring will be set to false by restoreSearchCriteria after delay
      
  } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      logError(error, "ResultsSearch.onMount", undefined);
    }
  });

  // Track if we've restored on this mount to avoid restoring multiple times
  let hasRestoredOnMount = false;
  let lastCustomerCode = "";
  
  // Save when component becomes visible again (when switching back to search tab)
  afterUpdate(() => {
    // Only restore once per mount cycle, and only if we haven't already restored
    // Also check if customer_code changed (component might be reused)
    if (!hasRestoredOnMount && !isRestoring && customer_code && customer_code !== lastCustomerCode) {
      const stored = sessionStorage.getItem(getStorageKey(customer_code));
      if (stored) {
        const parsed = JSON.parse(stored);
        // Only restore if searchQuery is empty or different from stored value
        if (searchQuery === "" || (parsed.text && searchQuery !== parsed.text)) {
          hasRestoredOnMount = true;
          lastCustomerCode = customer_code;
          restoreSearchCriteria();
          // fetchResults will be called by the date range reactive or onMount
        }
      }
    }
  });

  // Track when we last restored to prevent page reset
  let lastRestoreTime = 0;
  
  const debouncedQuerySearch = debounce(async () => {
    // Only reset to page 1 if not restoring and not recently restored (within 500ms)
    const timeSinceRestore = Date.now() - lastRestoreTime;
    if (!isRestoring && timeSinceRestore > 500) {
      currentPage = 1;
    }
    await fetchResults();
    if (!isRestoring) {
      saveSearchCriteria();
    }
  }, 300);


  function convertValueToFullyear(v: number) {
    const date = new Date(v, 11, 31); // Set to December 31st of the given year
    return date.toISOString();
  }

  function sortFacetOptions(options: FacetOption[], byCount: boolean) {
    return [...options].sort(
      (a, b) =>
        byCount
          ? b.count - a.count || String(a.value).localeCompare(String(b.value)) // Sort by count (desc) then alpha
          : String(a.value).localeCompare(String(b.value)) // Force string comparison
    );
  }

  $: if (searchQuery !== undefined && !isRestoring && hasFetchedAfterRestore) {
    // Only trigger debounced query if we've already done the post-restore fetch
    // This prevents double fetching during restoration
    debouncedQuerySearch();
    // Save immediately (not debounced) to ensure it's saved even if component is destroyed
    // Only save if we're not currently restoring
    saveSearchCriteria();
  }

  function updateVisiblePages() {
    const totalPages = Math.ceil(totalResults / resultsPerPage);
    const startPage = (currentBatch - 1) * batchSize + 1;
    const endPage = Math.min(startPage + batchSize - 1, totalPages);

    visiblePages = Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  }

  function handlePaginationAction(
    action:
      | "first"
      | "last"
      | "prev-batch"
      | "prev-page"
      | "next-page"
      | "next-batch"
      | number
  ) {
    try {
      const totalPages = Math.ceil(totalResults / resultsPerPage);
      const totalBatches = Math.ceil(totalPages / batchSize);
      let newPage = currentPage;

      switch (action) {
        case "first":
          newPage = 1;
          currentBatch = 1;
          break;

        case "last":
          newPage = totalPages;
          currentBatch = totalBatches;
          break;

        case "prev-batch":
          if (currentBatch > 1) {
            currentBatch--;
            newPage = currentBatch * batchSize;
          }
          break;

        case "prev-page":
          if (currentPage > 1) {
            newPage = currentPage - 1;
            if (newPage < visiblePages[0]) {
              currentBatch = Math.ceil(newPage / batchSize);
            }
          }
          break;

        case "next-page":
          if (currentPage < totalPages) {
            newPage = currentPage + 1;
            if (newPage > visiblePages[visiblePages.length - 1]) {
              currentBatch = Math.ceil(newPage / batchSize);
            }
          }
          break;

        case "next-batch":
          if (currentBatch < totalBatches) {
            currentBatch++;
            newPage = visiblePages[0] + batchSize;
          }
          break;

        default:
          if (typeof action === "number") {
            newPage = action;
            currentBatch = Math.ceil(newPage / batchSize);
          }
      }

    if (newPage !== currentPage) {
      currentPage = newPage;
      window.scrollTo(0, 0);
      saveSearchCriteria();
      fetchResults();
      }

      updateVisiblePages();
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      logError(error, "ResultsSearch.handlePaginationAction", undefined);
    }
  }

  // Update visible pages when total results or results per page changes
  $: if (totalResults || resultsPerPage) {
    updateVisiblePages();
  }

  function clearSearch() {
    try {
      searchQuery = "";
      fetchResults();
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      logError(error, "ResultsSearch.clearSearch", undefined);
    }
  }

  function getTextWidth(text: string) {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return 0;

    // Match your input's font exactly - adjust these values as needed
    context.font = "12px Inter, system-ui, -apple-system, sans-serif";
    const metrics = context.measureText(text);

    // Add padding for cursor space and general spacing
    const padding = 16; // Adjust this value to fine-tune positioning
    const letterSpacing = 0.16; // Adjust if your font has letter-spacing

    return metrics.width * (1 + letterSpacing) + padding;
  }

  function clearAllFacets() {
    try {
      selectedFacets = {};
      fetchResults();
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      logError(error, "ResultsSearch.clearAllFacets", undefined);
    }
  }

  $: {
    if (facetFilter !== undefined) {
      facets = [...facets]; // Force reactivity
      if (!isRestoring) {
        saveSearchCriteria();
      }
    }
  }

  function clearFacetFilter() {
    facetFilter = "";
  }

  function onViewClick(contentType: string, params: any) {
    try {
      const source = params.data.rawHit._source;
      const docId = params.data.id;
      const doc_key = docId; // Use docId as doc_key
      const file_name = `${doc_key}.${source.file_name.split(".").pop()}`; // Blob storage name
      const doc_name = getDocumentTitle(source); // User-friendly name
      
      // Look up total documents for this project from projectsData
      const project = projectsData?.projects?.find(p => p.project_id === source.project_id);
      const project_total_docs = project?.current_upload?.document_count;
      
      // Hyperlink clicks should start a new chat and set context to only this item
      selectedObjects.clearDocuments();
      selectedObjects.add({
        file_name: file_name, // Blob storage name (was doc_path)
        doc_name: doc_name, // User-friendly name (was doc_title)
        doc_key: doc_key, // Unique identifier (was key)
        customer_code: source.customer_code,
        project_id: source.project_id,
        project_name: source.project_name,
        document_id: docId,
        stage: source.kind, // 'kind' in ES maps to 'stage' in the app (Protocol, Screening, etc.)
        project_total_docs, // Total docs in project (enables full project detection)
      });

      // Trigger new chat signal
      newChatSignal.trigger();

      dispatch("showContent");
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      logError(error, "ResultsSearch.onViewClick", undefined);
    }
  }

  function getDocumentTitle(source: any): string {
    if (!source) return "Untitled"; // It should be impossible for source to be undefined, but just in case

    const content_type = source.attachment_content_type;
    const containsOfficeApp =
      content_type.toLowerCase().includes("excel") ||
      content_type.toLowerCase().includes("word") ||
      content_type.toLowerCase().includes("powerpoint");
    if (containsOfficeApp) {
      return source.file_name; // Excel, Word, PowerPoint files rarely have a good title in their metadata vs. the name of the file
    }
    if (
      source.attachment_title &&
      source.attachment_title.toLowerCase() !== "none"
    ) {
      return source.attachment_title + " [" + source.file_name + "]"; // PDFs should have a good title in their metadata
    }
    if (source.file_name) {
      return source.file_name;
    }
    return "Untitled"; // It should be impossible for source.file_name to be undefined, but just in case
  }

  function shouldShowOption(option: any): boolean {
    if (!facetFilter) return true;
    const searchTerm = facetFilter.toLowerCase().trim();
    const optionValue = option?.value?.toString().toLowerCase() ?? "";
    return optionValue.includes(searchTerm);
  }

  $: hasActiveFacets = Object.entries(selectedFacets).some(
    ([key, values]) => key !== "attachment_date" && values.length > 0
  );

  function hasVisibleOptions(facet: any): boolean {
    return facet.options.some((option: any) => shouldShowOption(option));
  }

  // Facets that should be hidden from display but still retrieved
  // These can be easily re-enabled by removing them from this list
  const HIDDEN_FACETS = [
    "study.keyword",
    "CNS_Tumor_Type.keyword",
    "Carcinoma_Type.keyword",
    "Leukemia_Type.keyword",
    "Lymphoma_Type.keyword",
    "Melanoma.keyword",
    "Sarcoma_Type.keyword",
    "Biomarker.keyword",
  ];

  function shouldDisplayFacet(facet: Facet): boolean {
    return !HIDDEN_FACETS.includes(facet.field);
  }

  function expandAllAccordions() {
    if (facetOpenState.length > 0) {
      facetOpenState.forEach((_, i) => (facetOpenState[i] = true));
      facetOpenState = [...facetOpenState];
    }
  }

  function collapseAllAccordions() {
    if (facetOpenState.length > 0) {
      facetOpenState.forEach((_, i) => (facetOpenState[i] = false));
      facetOpenState = [...facetOpenState];
    }
  }

  $: facets.forEach((facet) => initializeFacetVisibility(facet.field));

  function handleFacetChange(
    facetField: string,
    option: any,
    checked: boolean
  ) {
    try {
      selectedFacets[facetField] = selectedFacets[facetField] || [];

      if (checked) {
        selectedFacets[facetField] = [
          ...selectedFacets[facetField],
          option.value,
        ];
      } else {
        selectedFacets[facetField] = selectedFacets[facetField].filter(
          (val) => val !== option.value
        );
      }
      selectedFacets = selectedFacets;
      currentPage = 1;
      fetchResults();
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      logError(error, "ResultsSearch.handleFacetChange", undefined);
    }
  }

  // Replace the existing store subscription with:
  $: {
    // Re-run whenever the selected documents store changes
    $selectedObjects;
    if (results && results.length > 0) {
      // Force a re-render of results
      results = [...results];
    }
  }

  function isDocumentSelected(result: SearchResult) {
    const source = result._meta.rawHit._source;
    const doc_key = result._meta.id;
    const isSelected = selectedObjects.has(doc_key);
    return isSelected;
  }

  function handleDocumentChecked(e: Event, result: SearchResult) {
    try {
      const source = result._meta.rawHit._source;
      const doc_key = result._meta.id;
      const file_name = `${doc_key}.${source.file_name.split(".").pop()}`; // Blob storage name
      const doc_name = source.file_name; // User-friendly name
      const isChecked = (e.target as HTMLInputElement).checked;

      if (isChecked) {
        // Look up total documents for this project from projectsData
        const project = projectsData?.projects?.find(p => p.project_id === source.project_id);
        const project_total_docs = project?.current_upload?.document_count;

        selectedObjects.add({
          file_name: file_name, // Blob storage name (was doc_path)
          doc_name: doc_name, // User-friendly name (was doc_title)
          doc_key: doc_key, // Unique identifier (was key)
          customer_code: source.customer_code,
          project_id: source.project_id,
          project_name: source.project_name,
          document_id: doc_key,
          stage: source.kind, // 'kind' in ES maps to 'stage' in the app (Protocol, Screening, etc.)
          project_total_docs, // Total docs in project (enables full project detection)
        });
      } else {
        selectedObjects.remove(doc_key);
      }

    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      logError(error, "ResultsSearch.handleDocumentChecked", undefined);
    }
  }

  // Add this reactive statement to update the select all checkbox state
  $: {
    if (results && results.length > 0 && totalResults > 0) {
      // Check if all matching results (not just visible) are selected
      if (matchingDocumentKeys.size > 0) {
        // Use matching keys to check if all are selected
        const allMatchingSelected = Array.from(matchingDocumentKeys).every((doc_key) =>
          selectedObjects.has(doc_key)
        );
        const someMatchingSelected = Array.from(matchingDocumentKeys).some((doc_key) =>
          selectedObjects.has(doc_key)
        );

        allDocumentsChecked = allMatchingSelected && matchingDocumentKeys.size > 0;
        indeterminate = someMatchingSelected && !allMatchingSelected;
      } else {
        // Fallback: check visible results only if matching keys not available yet
        const totalVisible = results.length;
        const selectedVisible = results.filter((result) =>
          isDocumentSelected(result)
        ).length;

        allDocumentsChecked = selectedVisible === totalVisible && totalVisible > 0;
        indeterminate = selectedVisible > 0 && selectedVisible < totalVisible;
      }

      // Update the checkbox element directly
      const selectAllCheckbox = document.querySelector(
        ".option-checkbox.all"
      ) as HTMLInputElement;
      if (selectAllCheckbox) {
        selectAllCheckbox.checked = allDocumentsChecked;
        selectAllCheckbox.indeterminate = indeterminate;
      }
    } else {
      allDocumentsChecked = false;
      indeterminate = false;
    }
  }

  // Clear matching document keys when search criteria change
  $: if (searchQuery !== undefined || selectedFacets !== undefined || fromDate !== undefined || toDate !== undefined) {
    // Don't clear if we're restoring or initializing
    if (!isRestoring && isInitialized && hasFetchedAfterRestore) {
      // Clear matching keys when search changes
      matchingDocumentKeys = new Set();
      allDocumentsChecked = false;
      indeterminate = false;
    }
  }

  // Add this function after handleDocumentChecked
  async function handleAllDocumentChecked(e: Event) {
    try {
      const checkbox = e.target as HTMLInputElement;

      if (checkbox.checked) {
        // Select all matching documents (not just visible ones)
        isBulkSelecting = true;
        hasError = false;
        errorMessage = "";

        try {
          const allMatchingDocs = await fetchAllMatchingDocumentKeys();

          // Add all matching documents to selection
          allMatchingDocs.forEach((doc) => {
            if (!selectedObjects.has(doc.doc_key)) {
              // Look up total documents for this project from projectsData
              const project = projectsData?.projects?.find(p => p.project_id === doc.project_id);
              const project_total_docs = project?.current_upload?.document_count;

              selectedObjects.add({
                file_name: doc.file_name, // Blob storage name (was doc_path)
                doc_name: doc.doc_name, // User-friendly name (was doc_title)
                doc_key: doc.doc_key, // Unique identifier (was key)
                customer_code: doc.customer_code,
                project_id: doc.project_id,
                project_name: doc.project_name,
                document_id: doc.doc_key,
                stage: doc.stage, // Document stage (Protocol, Screening, etc.)
                project_total_docs, // Total docs in project (enables full project detection)
              });
            }
          });

          // Open the drawer and select all documents
          drawerStore.update((store) => ({
            ...store,
            activeDrawer: "selected-documents",
          }));

          allDocumentsChecked = true;
          indeterminate = false;

          // Dispatch event to show documents (only for visible ones to avoid performance issues)
          results.forEach((result) => {
            const source = result._meta.rawHit._source;
            const key = result._meta.id;
            const docLinkData = {
              project_id: source.project_id,
              customer_code: source.customer_code,
              doc_path: `${key}.${source.file_name.split(".").pop()}`,
              doc_title: getDocumentTitle(source),
            };
            dispatch("showDocument", docLinkData);
          });
        } catch (error) {
          const errorObj = error instanceof Error ? error : new Error(String(error));
          hasError = true;
          errorMessage = `Failed to select all documents: ${errorObj.message}`;
          logError(error, "ResultsSearch.handleAllDocumentChecked.selectAll", undefined);
          // Reset checkbox state on error
          checkbox.checked = false;
          allDocumentsChecked = false;
          indeterminate = false;
        } finally {
          isBulkSelecting = false;
        }

      } else {
        // Deselect all matching documents (use stored matching keys)
        if (matchingDocumentKeys.size > 0) {
          matchingDocumentKeys.forEach((doc_key) => {
            selectedObjects.remove(doc_key);
          });
        } else {
          // Fallback: deselect visible documents if matching keys not available
          results.forEach((result) => {
            const doc_key = result._meta.id;
            selectedObjects.remove(doc_key);
          });
        }

        allDocumentsChecked = false;
        indeterminate = false;
      }
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      isBulkSelecting = false;
      logError(error, "ResultsSearch.handleAllDocumentChecked", undefined);
    }
  }
</script>

<div class="search-container">
  <div class="search-top">
    <div class="mb-2 relative flex">
      <div class="relative flex items-center w-full">
        <Input
          defaultClass="search-input"
          type="text"
          placeholder="Search..."
          bind:value={searchQuery}
          on:blur={() => {
            if (!isRestoring) {
              saveSearchCriteria();
            }
          }}
        />
        {#if searchQuery}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div
            class="absolute clear-button clear-adjust"
            style="left: {getTextWidth(searchQuery)}px;"
            on:click={clearSearch}
          >
            <FontAwesomeIcon
              icon={faCircleXmark}
              class="w-4 h-4 text-gray-500 hover:text-gray-700"
            />
          </div>
        {/if}
      </div>
      <div class="ml-2 w-40">
        <span class="match-toggle-label">Match:</span>
        <ButtonGroup class="view-toggle-group">
          <Button
            class="match-toggle-button {toggleChecked === true ? 'active' : ''}"
            on:click={() => {
              toggleChecked = true;
              saveSearchCriteria();
              fetchResults();
            }}>ALL</Button
          >
          <Button
            class="match-toggle-button {toggleChecked === false
              ? 'active'
              : ''}"
            on:click={() => {
              toggleChecked = false;
              saveSearchCriteria();
              fetchResults();
            }}>ANY</Button
          >
        </ButtonGroup>
      </div>
    </div>
  </div>

  <div class="search-bottom">
    <section class="search-facets">
      <div class="sort-section">
        <div class="sort-label">Sort By</div>
        <select
          class="sort-select"
          bind:value={selectedSort}
          on:change={() => {
            currentPage = 1;
            saveSearchCriteria();
            fetchResults();
          }}
        >
          {#each sortOptions as option}
            <option class="sort-value" value={option.value}>
              {option.label}
            </option>
          {/each}
        </select>
      </div>

      <div class="facet-controls">
        <div class="facet-controls-header">
          <div class="filter-title">
            <span class="facet-label">Facets</span>
            {#if hasActiveFacets}
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <div class="clear-button" on:click={clearAllFacets}>
                <FontAwesomeIcon
                  icon={faCircleXmark}
                  class="w-4 h-4 text-gray-500 hover:text-gray-700"
                />
              </div>
            {/if}
          </div>
        </div>
        <div class="controls-row">
          <div class="relative flex items-center">
            <Input
              defaultClass="search-input"
              type="text"
              placeholder="Filter..."
              bind:value={facetFilter}
            />
            {#if facetFilter}
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <div
                class="absolute clear-button clear-adjust"
                style="left: {getTextWidth(facetFilter)}px;"
                on:click={clearFacetFilter}
              >
                <FontAwesomeIcon
                  icon={faCircleXmark}
                  class="w-4 h-4 text-gray-500 hover:text-gray-700"
                />
              </div>
            {/if}
          </div>
          <button
            class="icon-button"
            on:click={expandAllAccordions}
            title="Expand all"
          >
            <FontAwesomeIcon icon={faChevronDown} class="w-3 h-3" />
          </button>
          <button
            class="icon-button"
            on:click={collapseAllAccordions}
            title="Collapse all"
          >
            <FontAwesomeIcon icon={faChevronUp} class="w-3 h-3" />
          </button>
          <button
            class="icon-button"
            on:click={() => {
              facetSortByCount = !facetSortByCount;
              facets = facets.map((facet) => ({
                ...facet,
                options: sortFacetOptions(facet.options, facetSortByCount),
              }));
              saveSearchCriteria();
            }}
            title={facetSortByCount ? "Sort by name" : "Sort by count"}
          >
            {#if facetSortByCount}
              <FontAwesomeIcon icon={faArrowDown91} class="w-4 h-4" />
            {:else}
              <FontAwesomeIcon icon={faArrowDownAZ} class="w-4 h-4" />
            {/if}
          </button>
        </div>
      </div>
      <div class="facet-list-section">
        <Accordion multiple={true} flush={true}>
          <div class="pb-8">
            <span class="facet-header">Date Range</span>
            <RangeSlider
              step={1}
              id="circles"
              min={lowerYear}
              max={upperYear}
              pips
              range
              all="label"
              bind:values
            />
          </div>
          {#each facets as facet, i}
            {#if shouldDisplayFacet(facet) && hasVisibleOptions(facet)}
              {@const filteredOptions = facet.options.filter((option) =>
                shouldShowOption(option)
              )}
              <AccordionItem
                defaultClass="facet-group"
                bind:open={facetOpenState[i]}
                tag="div"
                paddingFlush="p-0 facet-accordion-item"
              >
                <svelte:fragment slot="arrowup">
                  <span style="display: none;"></span>
                </svelte:fragment>
                <svelte:fragment slot="arrowdown">
                  <span style="display: none;"></span>
                </svelte:fragment>
                <svelte:fragment slot="header">
                  <div class="facet-header-wrapper">
                    {#key `${i}-${facetOpenState[i]}`}
                    <FontAwesomeIcon
                      icon={facetOpenState[i] ? faChevronDown : faChevronRight}
                      class="facet-chevron-left"
                    />
                    {/key}
                    <span class="facet-header"
                      >{facet.label} ({visibleItems[facet.field] <
                      filteredOptions.length
                        ? visibleItems[facet.field]
                        : filteredOptions.length} of {filteredOptions.length})
                      {#if selectedFacets[facet.field] && Array.isArray(selectedFacets[facet.field]) && selectedFacets[facet.field].length > 0}
                        <FontAwesomeIcon 
                          icon={faFilter} 
                          class="facet-indicator-icon" 
                          title="This facet group has selected filters"
                        />
                      {/if}
                    </span>
                  </div>
                </svelte:fragment>
                <ul class="facet-list">
                  {#each filteredOptions.slice(0, visibleItems[facet.field]) as option}
                    <li class="facet-item">
                      <label 
                        class="option-group" 
                        class:active={selectedFacets[facet.field]?.includes(option.value)}
                        on:click={(e) => {
                          // Handle click on label (works for both button and text)
                          e.preventDefault();
                          const isChecked = selectedFacets[facet.field]?.includes(option.value);
                          handleFacetChange(facet.field, option, !isChecked);
                        }}
                      >
                        <button
                          type="button"
                          class="facet-filter-icon"
                          class:active={selectedFacets[facet.field]?.includes(option.value)}
                          aria-label={selectedFacets[facet.field]?.includes(option.value) 
                            ? `Remove filter: ${option.value}` 
                            : `Apply filter: ${option.value}`}
                        >
                          <FontAwesomeIcon icon={faFilter} />
                        </button>
                        <span class="option-name"
                          class:active={selectedFacets[facet.field]?.includes(option.value)}
                          >{option.value ? option.value : "Unknown"}</span
                        >
                        <span class="option-count">{option.count}</span>
                      </label>
                    </li>
                  {/each}
                </ul>
                {#if filteredOptions.length > visibleItems[facet.field]}
                  <div class="text-left ml-4 mb-1">
                    <a
                      href="javascript:void(0)"
                      class="text-xs"
                      on:click={(e) => showMoreItems(facet.field, e)}
                      style="color: #2576bc;"
                    >
                      + More
                    </a>
                  </div>
                {/if}
              </AccordionItem>
            {/if}
          {/each}
        </Accordion>
      </div>
    </section>

    <section class="search-results">
      {#if hasError}
        <div class="search-error-message">{errorMessage}</div>
      {:else}
        <!-- Always show toolbar, even when loading -->
        <div class="search-view-count">
          <div class="pager-showing">
            Showing <span class="pager-value"
              >{(currentPage - 1) * resultsPerPage + 1}</span
            >
            -
            <span class="pager-value"
              >{Math.min(currentPage * resultsPerPage, totalResults)}</span
            >
            of
            <span class="pager-value">{totalResults}</span> results
          </div>
          <div class="results-view-controls">
            <ButtonGroup class="view-toggle-group">
              <Button
                class="view-toggle-button {viewMode === 'compact'
                  ? 'active'
                  : ''}"
                on:click={() => {
                  viewMode = "compact";
                  saveSearchCriteria();
                }}
                ><FontAwesomeIcon icon={faMinus} /></Button
              >
              <Button
                class="view-toggle-button {viewMode === 'normal'
                  ? 'active'
                  : ''}"
                on:click={() => {
                  viewMode = "normal";
                  saveSearchCriteria();
                }}
                ><FontAwesomeIcon icon={faEquals} /></Button
              >
              <Button
                class="view-toggle-button {viewMode === 'expanded'
                  ? 'active'
                  : ''}"
                on:click={() => {
                  viewMode = "expanded";
                  saveSearchCriteria();
                }}
                ><FontAwesomeIcon icon={faBars} /></Button
              >
            </ButtonGroup>
            <select
              class="results-select"
              bind:value={resultsPerPage}
              on:change={() => {
                // Calculate which page should contain the first item currently being viewed
                const firstItemIndex = (currentPage - 1) * resultsPerPage + 1;
                currentPage = Math.ceil(firstItemIndex / resultsPerPage);
                saveSearchCriteria();
                fetchResults();
              }}
            >
              {#each [3, 5, 10, 25, 50, 75, 100] as value}
                <option {value}>{value}</option>
              {/each}
            </select>
            <input
              type="checkbox"
              class="option-checkbox all"
              checked={allDocumentsChecked}
              disabled={isBulkSelecting || isLoading}
              on:change={(e) => handleAllDocumentChecked(e)}
            />
          </div>
        </div>

        <!-- Show loading spinner below toolbar when fetching -->
        {#if isLoading || isInitialLoad}
          <div class="search-loading" style="display: flex; align-items: center; justify-content: center; gap: 0.5rem; padding: 1rem;">
            <FontAwesomeIcon icon={faSpinner} class="fa-spin" />
            <span>Fetching results...</span>
          </div>
        {:else if totalResults === 0}
          <div class="search-no-results">No results found</div>
        {:else}
          <div class="search-results-grid">
            {#each results as result}
            <Card class="search-results-card">
              <div class="card-header">
                <div class="flex justify-between items-top">
                  <a
                    class="document-link"
                    href="javascript:void(0)"
                    on:click={(e) => {
                      e.preventDefault();
                      onViewClick("document", { data: result._meta });
                    }}
                  >
                    <h5 class="title">
                      {getDocumentTitle(result._meta?.rawHit._source)}
                    </h5>
                  </a>
                  <div class="flex items-center gap-2">
                    <input
                      type="checkbox"
                      class="option-checkbox"
                      checked={isDocumentSelected(result)}
                      on:change={(e) => handleDocumentChecked(e, result)}
                    />
                  </div>
                </div>
              </div>
              <div class="card-item">
                <span class="value">
                  {result._meta?.rawHit._source?.project_name || "N/A"}
                </span>
              </div>

              {#if viewMode === "normal"}
                <div class="card-content">
                  <div class="card-column">
                    <div class="card-item">
                      <span class="label">Indication</span>
                      <span class="value"
                        >{result._meta?.rawHit._source?.indication ||
                          "N/A"}</span
                      >
                    </div>
                    <div class="card-item">
                      <span class="label">Study</span>
                      <span class="value"
                        >{result._meta?.rawHit._source?.study || "N/A"}</span
                      >
                    </div>
                  </div>
                  <div class="card-column">
                    <div class="card-item">
                      <span class="label">SLR Stage</span>
                      <span class="value">
                        {result._meta?.rawHit._source?.kind || "N/A"}
                      </span>
                    </div>
                    <div class="card-item">
                      <span class="label">File Name</span>
                      <span class="value">
                        {result._meta?.rawHit._source?.file_name || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              {/if}

              {#if viewMode === "expanded"}
                <div class="card-content">
                  <div class="card-column">
                    <div class="card-item">
                      <span class="label">Indication</span>
                      <span class="value"
                        >{result._meta?.rawHit._source?.indication ||
                          "N/A"}</span
                      >
                    </div>
                    <div class="card-item">
                      <span class="label">Study</span>
                      <span class="value"
                        >{result._meta?.rawHit._source?.study || "N/A"}</span
                      >
                    </div>
                    <div class="card-item">
                      <span class="label">Review Type</span>
                      <span class="value">
                        {result._meta?.rawHit._source?.review_type || "N/A"}
                      </span>
                    </div>
                  </div>

                  <div class="card-column">
                    <div class="card-item">
                      <span class="label">SLR Stage</span>
                      <span class="value">
                        {result._meta?.rawHit._source?.kind || "N/A"}
                      </span>
                    </div>
                    <div class="card-item">
                      <span class="label">File Name</span>
                      <span class="value"
                        >{result._meta?.rawHit._source?.file_name ||
                          "N/A"}</span
                      >
                    </div>
                    <div class="card-item">
                      <span class="label">File Type</span>
                      <span class="value"
                        >{result._meta?.rawHit._source
                          ?.attachment_content_type || "N/A"}</span
                      >
                    </div>
                  </div>
                </div>
                <div class="card-item">
                  <span class="label">Disease</span>
                  <span class="value">
                    {result._meta?.rawHit._source?.Disease || "N/A"}
                  </span>
                </div>
              {/if}

              <!-- Short (default) = title and project -->
              <!-- Normal = everything + email -->

              {#if searchQuery || extraQueryString}
                {(extraQueryString = "")}
                <div class="card-snippet">
                  {@html result._meta?.rawHit.highlight?.attachment_content?.join(
                    "... "
                  ) ||
                    result._meta?.rawHit._source?.attachment_content?.substring(
                      0,
                      500
                    ) + "..."}
                </div>
              {/if}

              <div class="card-footer">
                <!-- Footer content will go here -->
              </div>
            </Card>
            {/each}
          </div>
          <!-- <p class="mt-4">
            Selected range: {minValue} - {maxValue}
          </p> -->
          <div class="pagination-container">
            {#if totalResults > 0}
            <div class="pagination">
              <button
                class="pagination-button"
                disabled={currentPage === 1}
                on:click={() => handlePaginationAction("first")}
                title="First page"
              >
                <FontAwesomeIcon class="w-3 h-3" icon={faBackwardStep} />
              </button>

              <button
                class="pagination-button"
                disabled={currentBatch === 1}
                on:click={() => handlePaginationAction("prev-batch")}
                ><FontAwesomeIcon class="w-3 h-3" icon={faBackward} />
              </button>

              <button
                class="pagination-button"
                disabled={currentPage === 1}
                on:click={() => handlePaginationAction("prev-page")}
              >
                <FontAwesomeIcon class="w-3 h-3" icon={faCaretLeft} />
              </button>

              {#each visiblePages as page}
                <button
                  class="pagination-button {page === currentPage
                    ? 'active'
                    : ''}"
                  on:click={() => handlePaginationAction(page)}
                >
                  {page}
                </button>
              {/each}

              <button
                class="pagination-button"
                disabled={currentPage ===
                  Math.ceil(totalResults / resultsPerPage)}
                on:click={() => handlePaginationAction("next-page")}
              >
                <FontAwesomeIcon class="w-3 h-3" icon={faCaretRight} />
              </button>

              <button
                class="pagination-button"
                disabled={currentBatch ===
                  Math.ceil(
                    Math.ceil(totalResults / resultsPerPage) / batchSize
                  )}
                on:click={() => handlePaginationAction("next-batch")}
              >
                <FontAwesomeIcon class="w-3 h-3" icon={faForward} />
              </button>

              <button
                class="pagination-button"
                disabled={currentPage ===
                  Math.ceil(totalResults / resultsPerPage)}
                on:click={() => handlePaginationAction("last")}
                title="Last page"
              >
                <FontAwesomeIcon class="w-3 h-3" icon={faForwardStep} />
              </button>
            </div>
          {/if}
          </div>
        {/if}
      {/if}
    </section>
  </div>
</div>

